// Tracker core (ANALYTICS-SYSTEM-DESIGN.md §4–5).
//
// One tracker per (surface, toolId). Lifecycle:
//
//   createTracker() → GET /api/analytics/bootstrap
//        ├─ 404 / error / enabled:false → permanently inert (all no-ops)
//        └─ ok → consent adapter starts, session.start queued, batching begins
//
// Batching: flush at 20 queued events, on the server-provided interval, and on
// tab hide. ≤25 events per request (wire contract). fetch keepalive normally;
// navigator.sendBeacon on pagehide / visibilitychange-hidden (sendBeacon can't
// carry headers, so the beacon path passes the token as a query parameter).
// One retry per batch, then the batch is dropped — analytics must never queue
// unboundedly or block the configurator.

import { createConsentAdapter, newUuid, type ConsentAdapter } from "./consent";
import type {
  AnalyticsEvent,
  BootstrapResponse,
  ConsentState,
  ConsentStatus,
  DeviceClass,
  TrackerOptions,
  ViewportBucket,
} from "./types";

const MAX_EVENTS_PER_REQUEST = 25;
const FLUSH_AT_QUEUE_SIZE = 20;
const DEFAULT_FLUSH_INTERVAL_MS = 10_000;
const HEARTBEAT_INTERVAL_MS = 30_000;
/** Cap on events buffered while the bootstrap fetch is in flight. */
const PENDING_BUFFER_CAP = 50;

export interface TrackerState {
  surface: string;
  toolId: string | null;
  sessionId: string;
  phase: "pending" | "active" | "inert";
  consent: ConsentState | null;
  queued: number;
}

export interface Tracker {
  track(type: string, data?: Record<string, unknown>): void;
  setConsent(status: ConsentStatus, source?: string): void;
  getState(): TrackerState;
  /** Resolves once the bootstrap round-trip settled (active or inert). */
  whenReady(): Promise<void>;
  /** Force a flush (fetch path). Exposed for tests and hosts. */
  flush(): void;
  destroy(): void;
}

// ── environment probes (all safe under node / hostile pages) ────────────

function hasWindow(): boolean {
  return typeof window !== "undefined";
}

export function deviceClass(): DeviceClass {
  try {
    const coarse = window.matchMedia?.("(pointer: coarse)")?.matches ?? false;
    const w = window.innerWidth || 0;
    if (coarse && w < 768) return "mobile";
    if (coarse) return "tablet";
    return "desktop";
  } catch {
    return "desktop";
  }
}

export function viewportBucket(): ViewportBucket {
  try {
    const w = window.innerWidth || 0;
    if (w < 640) return "sm";
    if (w < 1024) return "md";
    if (w < 1440) return "lg";
    return "xl";
  } catch {
    return "md";
  }
}

function referrerHostname(): string | null {
  try {
    const ref = document.referrer;
    if (!ref) return null;
    return new URL(ref).hostname || null;
  } catch {
    return null;
  }
}

function pageHostname(): string {
  try {
    return window.location?.hostname ?? "";
  } catch {
    return "";
  }
}

// ── registry ─────────────────────────────────────────────────────────────

const trackers = new Map<string, Tracker>();

function trackerKey(surface: string, toolId?: string | null): string {
  return `${surface}|${toolId ?? ""}`;
}

/** Returns an existing tracker. With no arguments returns the first (usually
 *  only) tracker on the page. */
export function getTracker(surface?: string, toolId?: string | null): Tracker | null {
  if (surface === undefined) {
    const first = trackers.values().next();
    return first.done ? null : first.value;
  }
  return trackers.get(trackerKey(surface, toolId)) ?? null;
}

export function getAllTrackers(): Tracker[] {
  return Array.from(trackers.values());
}

/** Test-only. */
export function _resetTrackersForTests(): void {
  for (const t of Array.from(trackers.values())) t.destroy();
  trackers.clear();
}

/** Shape of the window.DConfigAnalytics integration API. */
export interface DConfigAnalyticsGlobal {
  /** Pushes an event into every active tracker on the page. */
  track(type: string, data?: Record<string, unknown>): void;
  /** Programmatic consent override for every active tracker. */
  setConsent(status: ConsentStatus, source?: string): void;
  /** Snapshot of each tracker's state. */
  getState(): TrackerState[];
}

/**
 * Attaches `window.DConfigAnalytics` (idempotent) so tools, host pages, and
 * integrators can push events without importing the module. Events route to
 * every active tracker — pages normally run exactly one. Called automatically
 * by createTracker.
 */
export function installGlobalApi(): void {
  try {
    if (typeof window === "undefined") return;
    const w = window as unknown as {
      DConfigAnalytics?: DConfigAnalyticsGlobal & { __dconfig?: boolean };
    };
    if (w.DConfigAnalytics && w.DConfigAnalytics.__dconfig === true) return;
    w.DConfigAnalytics = {
      __dconfig: true,
      track: (type: string, data?: Record<string, unknown>) => {
        for (const t of getAllTrackers()) t.track(type, data);
      },
      setConsent: (status: ConsentStatus, source?: string) => {
        for (const t of getAllTrackers()) t.setConsent(status, source);
      },
      getState: () => getAllTrackers().map((t) => t.getState()),
    } as DConfigAnalyticsGlobal & { __dconfig: boolean };
  } catch {
    /* never break the host page */
  }
}

// ── tracker ──────────────────────────────────────────────────────────────

/**
 * Creates (or returns the existing) tracker for a (surface, toolId) pair.
 * Never throws; a broken environment produces an inert tracker.
 */
export function createTracker(opts: TrackerOptions): Tracker {
  const key = trackerKey(opts.surface, opts.toolId);
  const existing = trackers.get(key);
  if (existing) return existing;

  installGlobalApi();

  let phase: "pending" | "active" | "inert" = "pending";
  let token = "";
  let flushIntervalMs = DEFAULT_FLUSH_INTERVAL_MS;
  let adapter: ConsentAdapter | null = null;
  let destroyed = false;

  const sessionId = newUuid();
  const queue: AnalyticsEvent[] = [];
  /** Events tracked before bootstrap settles. */
  const pendingBuffer: { type: string; data: Record<string, unknown>; ts: number }[] = [];

  let flushTimer: ReturnType<typeof setInterval> | null = null;
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  let interactedSinceLastBeat = false;
  let hasInteracted = false;
  let engagedSeconds = 0;
  let sessionEnded = false;

  const teardownFns: (() => void)[] = [];

  const eventsUrl = () => `${opts.apiBase}/api/analytics/events`;

  const buildEvent = (type: string, data: Record<string, unknown>, ts?: number): AnalyticsEvent => ({
    type,
    ts: ts ?? Date.now(),
    sessionId,
    visitorId: adapter?.getVisitorId() ?? null,
    surface: opts.surface,
    toolId: opts.toolId ?? null,
    market: opts.market ?? null,
    language: opts.language ?? null,
    device: deviceClass(),
    viewport: viewportBucket(),
    data,
  });

  /** POST one batch. Retries once on any failure, then drops. */
  const sendBatch = (events: AnalyticsEvent[], attempt: number): void => {
    try {
      fetch(eventsUrl(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Analytics-Token": token,
        },
        body: JSON.stringify({ events }),
        keepalive: true,
      })
        .then((res) => {
          if (!res.ok && attempt === 0) retryLater(events);
        })
        .catch(() => {
          if (attempt === 0) retryLater(events);
        });
    } catch {
      if (attempt === 0) retryLater(events);
    }
  };

  const retryLater = (events: AnalyticsEvent[]): void => {
    if (destroyed) return;
    const t = setTimeout(() => sendBatch(events, 1), 1000);
    teardownFns.push(() => clearTimeout(t));
  };

  const flush = (useBeacon = false): void => {
    if (phase !== "active" || queue.length === 0) return;
    while (queue.length > 0) {
      const batch = queue.splice(0, MAX_EVENTS_PER_REQUEST);
      if (useBeacon) {
        let ok = false;
        try {
          if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
            // sendBeacon can't set headers — token travels as a query param.
            const url = `${eventsUrl()}?token=${encodeURIComponent(token)}`;
            const blob = new Blob([JSON.stringify({ events: batch })], {
              type: "application/json",
            });
            ok = navigator.sendBeacon(url, blob);
          }
        } catch {
          ok = false;
        }
        if (!ok) sendBatch(batch, 1); // last-ditch keepalive fetch, no retry
      } else {
        sendBatch(batch, 0);
      }
    }
  };

  const enqueue = (type: string, data: Record<string, unknown>, ts?: number): void => {
    if (destroyed || sessionEnded) return;
    if (phase === "inert") return;
    if (phase === "pending") {
      if (pendingBuffer.length < PENDING_BUFFER_CAP) {
        pendingBuffer.push({ type, data, ts: ts ?? Date.now() });
      }
      return;
    }
    // Tier 0 (essential-only) drops all JS events; tiers 1–2 send.
    if ((adapter?.getState().tier ?? 1) === 0) return;
    queue.push(buildEvent(type, data, ts));
    if (queue.length >= FLUSH_AT_QUEUE_SIZE) flush();
  };

  const goInert = (reason: string): void => {
    phase = "inert";
    pendingBuffer.length = 0;
    queue.length = 0;
    stopTimersAndListeners();
    try {
      // The single permitted line of console noise.
      console.debug(`[dconfig-analytics] disabled (${reason})`);
    } catch {
      /* noop */
    }
  };

  const stopTimersAndListeners = (): void => {
    if (flushTimer !== null) {
      clearInterval(flushTimer);
      flushTimer = null;
    }
    if (heartbeatTimer !== null) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
    for (const fn of teardownFns.splice(0)) {
      try {
        fn();
      } catch {
        /* noop */
      }
    }
  };

  const onVisibilityChange = (): void => {
    try {
      if (document.visibilityState === "hidden") flush(true);
    } catch {
      /* noop */
    }
  };

  const onPageHide = (): void => {
    endSession();
  };

  const endSession = (): void => {
    if (sessionEnded || phase !== "active") return;
    if ((adapter?.getState().tier ?? 1) !== 0) {
      // Delta semantics: only the engaged time not yet reported by a heartbeat.
      // A partial beat interval with interaction counts as half an interval.
      const tailDelta = interactedSinceLastBeat ? HEARTBEAT_INTERVAL_MS / 2000 : 0;
      queue.push(
        buildEvent("session.end", {
          engaged: tailDelta,
          interacted: hasInteracted,
        }),
      );
    }
    flush(true);
    sessionEnded = true;
  };

  const onInteraction = (): void => {
    interactedSinceLastBeat = true;
    hasInteracted = true;
  };

  const activate = (bootstrap: BootstrapResponse): void => {
    token = bootstrap.token;
    flushIntervalMs = bootstrap.flushIntervalMs && bootstrap.flushIntervalMs > 0
      ? bootstrap.flushIntervalMs
      : DEFAULT_FLUSH_INTERVAL_MS;

    adapter = createConsentAdapter({
      config: bootstrap.consent,
      explicitOverride: opts.consentOverride,
      language: opts.language,
      apiBase: opts.apiBase,
      sessionId,
      bannerContainer: opts.bannerContainer ?? null,
    });

    phase = "active";

    // session.start first, then anything buffered while bootstrap was in flight.
    enqueue("session.start", {
      referrer: referrerHostname(),
      device: deviceClass(),
      viewport: viewportBucket(),
    });
    for (const buffered of pendingBuffer.splice(0)) {
      enqueue(buffered.type, buffered.data, buffered.ts);
    }

    // Interval flush.
    flushTimer = setInterval(() => flush(), flushIntervalMs);

    // Heartbeat: every 30 s, only while the document is visible AND the user
    // interacted since the previous beat. The server sums `engaged` as a
    // per-event DELTA (seconds since the previous report), never a total.
    heartbeatTimer = setInterval(() => {
      try {
        if (document.visibilityState !== "visible") return;
      } catch {
        return;
      }
      if (!interactedSinceLastBeat) return;
      interactedSinceLastBeat = false;
      const delta = HEARTBEAT_INTERVAL_MS / 1000;
      engagedSeconds += delta;
      enqueue("session.heartbeat", { engaged: delta });
    }, HEARTBEAT_INTERVAL_MS);

    // Interaction + lifecycle listeners.
    try {
      const interactionEvents = ["pointerdown", "keydown", "wheel", "touchstart", "scroll"];
      for (const name of interactionEvents) {
        window.addEventListener(name, onInteraction, { capture: true, passive: true });
      }
      teardownFns.push(() => {
        for (const name of interactionEvents) {
          window.removeEventListener(name, onInteraction, { capture: true });
        }
      });

      document.addEventListener("visibilitychange", onVisibilityChange);
      teardownFns.push(() => document.removeEventListener("visibilitychange", onVisibilityChange));

      window.addEventListener("pagehide", onPageHide);
      teardownFns.push(() => window.removeEventListener("pagehide", onPageHide));
    } catch {
      /* environment without window/document — timers still work */
    }
  };

  // ── bootstrap fetch ────────────────────────────────────────────────────
  const readyPromise: Promise<void> = (async () => {
    if (!hasWindow()) {
      goInert("no window");
      return;
    }
    try {
      const params = new URLSearchParams({ surface: opts.surface });
      if (opts.toolId) params.set("toolId", opts.toolId);
      params.set("origin", pageHostname());
      const res = await fetch(`${opts.apiBase}/api/analytics/bootstrap?${params.toString()}`, {
        method: "GET",
      });
      if (!res.ok) {
        goInert(`bootstrap ${res.status}`);
        return;
      }
      const body = (await res.json()) as BootstrapResponse;
      if (!body || body.enabled !== true || !body.token) {
        goInert("not enabled");
        return;
      }
      if (destroyed) return;
      activate(body);
    } catch {
      goInert("bootstrap unreachable");
    }
  })();

  const tracker: Tracker = {
    track: (type, data) => enqueue(type, data ?? {}),
    setConsent: (status, source) => adapter?.setConsent(status, source),
    getState: () => ({
      surface: opts.surface,
      toolId: opts.toolId ?? null,
      sessionId,
      phase,
      consent: adapter?.getState() ?? null,
      queued: queue.length,
    }),
    whenReady: () => readyPromise,
    flush: () => flush(),
    destroy: () => {
      destroyed = true;
      stopTimersAndListeners();
      adapter?.destroy();
      adapter = null;
      queue.length = 0;
      pendingBuffer.length = 0;
      trackers.delete(key);
    },
  };

  trackers.set(key, tracker);
  return tracker;
}
